import { Button, FieldBlock, FilterEditor, Input, Modal, RichTextEditor, Select, Textarea, useToast } from '@orbind/ui'
import {
  MASCARAS_PRONTAS,
  chavesDoTemplate,
  layoutDaEscolha,
  mascaraDoCampo,
  montarUrlDoBotao,
  type LayoutEscolha,
  type SavedFilter,
} from '@orbind/core'
import { useQuery } from '@tanstack/react-query'
import { useEffect, useMemo, useRef, useState } from 'react'
import { api } from '../../lib/api.ts'
import { CatalogIcon, IconField } from '../../components/IconPicker.tsx'
import { useConfirm } from '../work-item/bits.tsx'
import { useT } from '../../lib/i18n.tsx'
import {
  TIPOS_CAMPO,
  fieldImpact,
  useAddPlacement,
  useCustomFields,
  useDeleteField,
  useDeleteGroup,
  useRemovePlacement,
  useSaveField,
  useSaveGroup,
  useSaveLayout,
  useSavePlacement,
  viewTypeDe,
  type CustomField,
  type CustomFieldGroup,
  type CustomFieldPlacement,
  type ViewType,
} from './custom-field-data.ts'
import { CatalogoCampos, DRAG_MIME } from './CatalogoCampos.tsx'
import { ListaDeCampos } from './ListaDeCampos.tsx'
import { AbaLayoutEditor } from './AbaLayoutEditor.tsx'
import { camposParaCriterios, regraDoConfig, regraVazia } from './custom-field-rules.ts'
import './campos-complementares.css'

/**
 * Campos complementares (SPEC-20260828-1914) — fase 1, entidade Item de Trabalho.
 * Construtor: campos por tipo de item, na barra de propriedades ou em abas.
 * As abas aparecem como abas de verdade (nome + ícone + descrição), e cada
 * aba tem seus campos — igual ao que o usuário vê no detalhe do item.
 */

const rotuloDe = (labels: Record<string, string>, fallback: string) =>
  labels['pt-BR'] || Object.values(labels)[0] || fallback
const chaveDe = (texto: string) =>
  texto
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .replace(/^([0-9])/, 'c_$1')
    .slice(0, 60) || 'campo'

/** Ícone de exibição de um grupo: imagem própria (id) ou classe do Font Awesome. */
const iconeDoGrupo = (config: Record<string, unknown> | undefined) => {
  const id = config?.['iconImageId'] as string | undefined
  return id ? `/public/catalog-icon/${id}` : ((config?.['icon'] as string) || '')
}
const descricaoDoGrupo = (config: Record<string, unknown> | undefined) =>
  (config?.['description'] as string) || ''

type Entidade = 'work_item' | 'company' | 'person'
type Rascunho = {
  id?: string
  entity: Entidade
  label: string
  key: string
  type: CustomField['type']
  required: boolean
  defaultVisible: boolean
  opcoes: string
  /** Máscara do texto curto: chave de `MASCARAS_PRONTAS` ou `custom`; '' = sem máscara. */
  mascara: string
  /** Padrão livre, usado só quando `mascara === 'custom'`. */
  mascaraPadrao: string
  /** Instruções de preenchimento (HTML) — o PADRÃO do "i" nos itens. */
  instrucoes: string
  /** Pasta do catálogo onde o campo vai morar; null = solto. */
  catalogGroupId: string | null
  /** Formato da escolha: menu suspenso ('list') ou tags ('tag'). */
  layout: LayoutEscolha
  /** Endereço do campo botão; aceita {chave} de outro campo. */
  botaoUrl: string
  /** Texto do campo botão. */
  botaoRotulo: string
  /**
   * Criado pelo "+" de um TÓPICO (ou da barra, com null): ao salvar, o campo já
   * entra ali, sem precisar arrastar do catálogo (pedido 2026-08-30).
   * `undefined` = criado pelo catálogo, sem colocação automática.
   */
  destinoGrupoId?: string | null
}

const vazio = (entity: Entidade = 'work_item'): Rascunho => ({
  entity,
  label: '',
  key: '',
  type: 'text',
  required: false,
  defaultVisible: true,
  opcoes: '',
  mascara: '',
  mascaraPadrao: '',
  instrucoes: '',
  catalogGroupId: null,
  layout: 'list',
  botaoUrl: '',
  botaoRotulo: '',
})

/** Onde a largura escolhida para o catálogo fica guardada. */
const CAT_LARGURA_CHAVE = 'orbind:campos:catalogo-largura'
/**
 * Limites da largura do catálogo. O teto acompanha a janela (até 70% dela),
 * porque num monitor largo faz sentido abrir bem o painel; o piso garante que
 * o construtor continue utilizável.
 */
const limitaLargura = (px: number) =>
  Math.round(Math.min(Math.max(560, window.innerWidth * 0.7), Math.max(220, px)))

/** Formatos de um campo de escolha — mesma configuração, aparência diferente. */
const LAYOUTS_ESCOLHA: { value: LayoutEscolha; label: string; icon: string }[] = [
  { value: 'list', label: 'Lista', icon: 'fas fa-caret-square-down' },
  { value: 'tag', label: 'Tag', icon: 'fas fa-tags' },
]

/** Opcoes do seletor de entidade do TOPICO (fase 6). */
const ENTIDADES: { value: Entidade; label: string; icon: string; hint: string }[] = [
  { value: 'work_item', label: 'Item de Trabalho', icon: 'fas fa-tasks', hint: 'Aparece no item, por tipo.' },
  { value: 'company', label: 'Empresa', icon: 'fas fa-building', hint: 'Aparece no cadastro da empresa.' },
  { value: 'person', label: 'Pessoa', icon: 'fas fa-user', hint: 'Aparece no cadastro da pessoa.' },
]

const ENTIDADE_LABEL: Record<Entidade, string> = {
  work_item: 'Item de Trabalho',
  company: 'Empresa',
  person: 'Pessoa',
}

/** Rótulo curto do tipo de visualização, para o badge no cabeçalho do tópico. */
const MODO_ROTULO: Record<ViewType, string> = {
  simple: 'Simples',
  tab: 'Aba',
  form: 'Formulário',
}

type GrupoRascunho = {
  id?: string
  /**
   * A que entidade o tópico pertence (fase 6): é ELE que liga o campo ao Item
   * de Trabalho, à Empresa ou à Pessoa. Empresa e Pessoa não têm 'tipo'.
   */
  entity: Entidade
  nome: string
  icon: string
  iconImageId: string | null
  descricao: string
  /** Instrucoes de preenchimento do topico (HTML) — padrao do "i" no item. */
  instrucoes: string
  /** Tipo de visualização: simples (tópico solto), aba (lado a lado) ou formulário (botão + modal). */
  viewType: ViewType
  /** Tipos de item a que o tópico pertence; vazio = todos os tipos. */
  workItemTypeIds: string[]
  visibilityRule: SavedFilter
}

export function CamposComplementaresSection({ criarSinal = 0 }: { criarSinal?: number }) {
  const t = useT()
  const toast = useToast()
  const confirm = useConfirm()
  const boot = useQuery({
    queryKey: ['catalog', 'bootstrap'],
    queryFn: async () => (await api.catalog.bootstrap.$get()).json(),
  })
  /**
   * Entidade em edição (fase 6): a tela inteira — tópicos, campos e catálogo —
   * opera sobre ELA. Sem isto, um tópico de Empresa/Pessoa era criado mas não
   * aparecia em lugar nenhum da interface.
   */
  const [ENTITY, setEntidadeAtiva] = useState<Entidade>('work_item')
  const dados = useCustomFields(ENTITY)
  const salvarCampo = useSaveField(ENTITY)
  const apagarCampo = useDeleteField(ENTITY)
  const salvarGrupo = useSaveGroup(ENTITY)
  const apagarGrupo = useDeleteGroup(ENTITY)
  const adicionarColocacao = useAddPlacement(ENTITY)
  const removerColocacao = useRemovePlacement(ENTITY)
  const salvarLayout = useSaveLayout(ENTITY)
  const salvarConfigColocacao = useSavePlacement(ENTITY)

  // modal de condições de UMA colocação (campo dentro de uma aba/barra)
  const [regrasColoc, setRegrasColoc] = useState<{
    id: string
    nome: string
    visibilityRule: SavedFilter
    requiredRule: SavedFilter
  } | null>(null)

  const [rascunho, setRascunho] = useState<Rascunho | null>(null)
  const [chaveTocada, setChaveTocada] = useState(false)
  /** Lista completa do catálogo ocupando a área de trabalho (2026-08-30). */
  const [verLista, setVerLista] = useState(false)
  const [grupoRascunho, setGrupoRascunho] = useState<GrupoRascunho | null>(null)
  /**
   * Accordions recolhidos: 'prop' (barra de propriedades) ou o id do grupo.
   * A tela ABRE com todos fechados (pedido 2026-08-31): a lista de tópicos
   * cresce e, aberta, obrigava a rolar muito para achar o que se procura.
   */
  const [recolhidos, setRecolhidos] = useState<ReadonlySet<string>>(new Set())
  const jaFechouTudo = useRef(false)
  useEffect(() => {
    if (jaFechouTudo.current) return
    const ids = (dados.data?.groups ?? []).filter((g) => g.kind === 'topic').map((g) => g.id)
    if (ids.length === 0) return
    jaFechouTudo.current = true
    setRecolhidos(new Set(['prop', ...ids]))
  }, [dados.data?.groups])
  // accordion sob o cursor num arraste (destaque do alvo de soltar)
  const [sobre, setSobre] = useState<string | null>(null)
  // largura do painel de catálogo (px); divisor arrastável entre as colunas
  /**
   * Largura do catálogo, lembrada entre sessões — quem trabalha com nomes de
   * campo longos alarga uma vez e não quer refazer isso a cada visita.
   */
  const [catLargura, setCatLargura] = useState(() => {
    const salvo = Number(window.localStorage.getItem(CAT_LARGURA_CHAVE))
    return Number.isFinite(salvo) && salvo > 0 ? salvo : 300
  })
  useEffect(() => {
    window.localStorage.setItem(CAT_LARGURA_CHAVE, String(catLargura))
  }, [catLargura])
  const arrasteDivisor = useRef<{ x: number; larg: number } | null>(null)
  const aoArrastarDivisor = (e: React.PointerEvent) => {
    if (!arrasteDivisor.current) return
    // arrastar para a ESQUERDA aumenta o catálogo; para a direita, diminui
    const delta = arrasteDivisor.current.x - e.clientX
    setCatLargura(limitaLargura(arrasteDivisor.current.larg + delta))
  }
  /** Duplo clique no divisor: alterna entre o tamanho padrão e o largo. */
  const alternarLarguraCatalogo = () => setCatLargura((l) => (l > 460 ? 300 : limitaLargura(760)))
  const trocarEntidade = (e: Entidade) => {
    setEntidadeAtiva(e)
    setRascunho(null)
    setGrupoRascunho(null)
    setRecolhidos(new Set())
  }

  const alternar = (id: string) =>
    setRecolhidos((s) => {
      const n = new Set(s)
      if (n.has(id)) n.delete(id)
      else n.add(id)
      return n
    })

  useEffect(() => {
    if (criarSinal > 0) {
      setRascunho(vazio(ENTITY))
      setChaveTocada(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [criarSinal])

  const campos = dados.data?.fields ?? []
  const grupos = useMemo(
    () => (dados.data?.groups ?? []).filter((g) => g.kind === 'topic'),
    [dados.data?.groups],
  )
  const placements = dados.data?.placements ?? []
  const campoPorId = useMemo(() => new Map(campos.map((c) => [c.id, c])), [campos])

  /** Colocações de um grupo (ou da barra quando groupId=null), com o campo. */
  const colocacoesDe = (groupId: string | null) =>
    placements
      .filter((p) => p.groupId === groupId)
      .map((p) => ({ placement: p, campo: campoPorId.get(p.fieldId) }))
      .filter((x): x is { placement: CustomFieldPlacement; campo: CustomField } => Boolean(x.campo))

  /** Solta um campo do catálogo numa aba/barra: cria a colocação. */
  const soltarNoGrupo = (groupId: string | null) => (e: React.DragEvent) => {
    e.preventDefault()
    setSobre(null)
    const bruto = e.dataTransfer.getData(DRAG_MIME)
    if (!bruto) return
    let dado: { fieldId: string; entity: string }
    try {
      dado = JSON.parse(bruto)
    } catch {
      return
    }
    if (dado.entity !== ENTITY) {
      toast.push({ tone: 'warning', message: `Só campos de ${ENTIDADE_LABEL[ENTITY]} entram aqui.` })
      return
    }
    adicionarColocacao.mutate(
      { fieldId: dado.fieldId, groupId },
      {
        onSuccess: (r) =>
          toast.push({
            tone: (r as { already?: boolean }).already ? 'info' : 'success',
            message: (r as { already?: boolean }).already ? 'O campo já está aqui.' : 'Campo adicionado.',
          }),
        onError: (err) => toast.push({ tone: 'danger', message: err instanceof Error ? err.message : 'Falha ao adicionar.' }),
      },
    )
  }

  // campos disponíveis nos critérios das regras (complementares + nativos)
  const camposCriterio = useMemo(
    () => camposParaCriterios(campos, rotuloDe, rascunho?.id),
    [campos, rascunho?.id],
  )
  // opções de valor para os campos de seleção (nativos e complementares)
  const carregarOpcoes = (fieldKey: string, query: string) => {
    const q = query.trim().toLowerCase()
    const b = boot.data as
      | {
          workItemTypes?: { id: string; nameTranslationKey: string; code: string }[]
          workItemStatuses?: { id: string; nameTranslationKey: string; code: string }[]
          priorities?: { id: string; nameTranslationKey: string; code: string }[]
          areas?: { id: string; nameTranslationKey: string; code: string }[]
          companies?: { id: string; name: string }[]
        }
      | undefined
    const lab = (x: { nameTranslationKey: string; code: string }) => {
      const l = t(x.nameTranslationKey)
      return l === x.nameTranslationKey ? x.code : l
    }
    const cat = (xs: { id: string; nameTranslationKey: string; code: string }[] | undefined) =>
      (xs ?? []).map((x) => ({ value: x.id, label: lab(x) }))
    let opts: { value: string; label: string }[] = []
    if (fieldKey === 'sys_type') opts = cat(b?.workItemTypes)
    else if (fieldKey === 'sys_status') opts = cat(b?.workItemStatuses)
    else if (fieldKey === 'sys_priority') opts = cat(b?.priorities)
    else if (fieldKey === 'sys_area') opts = cat(b?.areas)
    else if (fieldKey === 'sys_company') opts = (b?.companies ?? []).map((x) => ({ value: x.id, label: x.name }))
    else {
      const cf = campos.find((c) => c.key === fieldKey)
      if (cf) opts = cf.config.options ?? []
    }
    return opts.filter((o) => o.label.toLowerCase().includes(q))
  }

  const abrirEdicao = (c: CustomField) => {
    setChaveTocada(true)
    setRascunho({
      id: c.id,
      entity: (c.entity as Entidade) ?? 'work_item',
      label: rotuloDe(c.labels, c.key),
      key: c.key,
      type: c.type,
      required: c.required,
      defaultVisible: c.defaultVisible,
      opcoes: (c.config.options ?? []).map((o) => o.label).join('\n'),
      mascara: typeof c.config.mask === 'string' ? c.config.mask : '',
      mascaraPadrao: typeof c.config.maskPattern === 'string' ? c.config.maskPattern : '',
      instrucoes: typeof c.config.help === 'string' ? c.config.help : '',
      catalogGroupId: c.catalogGroupId ?? null,
      layout: layoutDaEscolha(c.config),
      botaoUrl: typeof c.config.buttonUrl === 'string' ? c.config.buttonUrl : '',
      botaoRotulo: typeof c.config.buttonLabel === 'string' ? c.config.buttonLabel : '',
    })
  }

  const salvar = () => {
    if (!rascunho) return
    const label = rascunho.label.trim()
    if (!label) return toast.push({ tone: 'danger', message: 'Informe o rótulo do campo.' })
    const key = (rascunho.key || chaveDe(label)).trim()
    const precisaOpcoes = rascunho.type === 'select' || rascunho.type === 'multiselect'
    const options = precisaOpcoes
      ? rascunho.opcoes
          .split('\n')
          .map((s) => s.trim())
          .filter(Boolean)
          .map((s) => ({ value: chaveDe(s), label: s }))
      : []
    const config: Record<string, unknown> = {}
    if (precisaOpcoes) {
      config.options = options
      // só o layout muda entre lista e tag: as opções acima são as mesmas
      if (rascunho.layout === 'tag') config.layout = 'tag'
    }
    if (rascunho.type === 'button') {
      config.buttonUrl = rascunho.botaoUrl.trim()
      config.buttonLabel = rascunho.botaoRotulo.trim()
    }
    // máscara só faz sentido em texto curto; "personalizada" sem padrão vira sem máscara
    if (rascunho.type === 'text' && rascunho.mascara) {
      const padrao = rascunho.mascaraPadrao.trim()
      if (rascunho.mascara !== 'custom' || padrao) {
        config.mask = rascunho.mascara
        if (rascunho.mascara === 'custom') config.maskPattern = padrao
      }
    }
    // instrucoes de preenchimento: o PADRAO do "i" — cada item pode
    // personalizar a sua por cima (2026-09-01)
    if (rascunho.instrucoes.trim() !== '' && rascunho.instrucoes !== '<p></p>') config.help = rascunho.instrucoes
    salvarCampo.mutate(
      {
        id: rascunho.id,
        entity: rascunho.entity,
        catalogGroupId: rascunho.catalogGroupId,
        key,
        labels: { 'pt-BR': label },
        type: rascunho.type,
        required: rascunho.required,
        defaultVisible: rascunho.defaultVisible,
        config,
      } as never,
      {
        onSuccess: (dados) => {
          // criado pelo "+" de um tópico/barra: já coloca o campo lá
          const novoId = (dados as { field?: { id?: string } } | undefined)?.field?.id
          if (rascunho.destinoGrupoId !== undefined && !rascunho.id && novoId) {
            adicionarColocacao.mutate({ fieldId: novoId, groupId: rascunho.destinoGrupoId } as never)
          }
          setRascunho(null)
          toast.push({ tone: 'success', message: 'Campo salvo.' })
        },
        onError: (e) => toast.push({ tone: 'danger', message: e instanceof Error ? e.message : 'Falha ao salvar.' }),
      },
    )
  }

  const removerCampo = async (c: CustomField) => {
    let n = 0
    try {
      n = (await fieldImpact(c.id)).itemsComValor
    } catch {
      /* segue com aviso generico */
    }
    confirm.ask(
      `O campo "${rotuloDe(c.labels, c.key)}" será apagado.` +
        (n > 0 ? ` ${n} Item(ns) de Trabalho têm valor nele — esses valores serão PERDIDOS.` : ' Nenhum item tem valor nele.'),
      () => apagarCampo.mutate(c.id),
    )
  }

  // --------------------------------------------------------------- abas
  const novaAba = () =>
    setGrupoRascunho({
      entity: ENTITY,
      nome: '',
      icon: 'fas fa-folder',
      iconImageId: null,
      descricao: '',
      instrucoes: '',
      viewType: 'simple',
      workItemTypeIds: [],
      visibilityRule: regraVazia('visibility'),
    })
  const editarAba = (g: CustomFieldGroup) =>
    setGrupoRascunho({
      id: g.id,
      nome: rotuloDe(g.labels, 'Tópico'),
      icon: (g.config?.['icon'] as string) || '',
      iconImageId: (g.config?.['iconImageId'] as string) || null,
      descricao: descricaoDoGrupo(g.config),
      instrucoes: typeof g.config?.['help'] === 'string' ? (g.config['help'] as string) : '',
      viewType: viewTypeDe(g.config),
      entity: (g.entity as Entidade) ?? 'work_item',
      workItemTypeIds: Array.isArray(g.config?.['workItemTypeIds']) ? (g.config!['workItemTypeIds'] as string[]) : [],
      visibilityRule: regraDoConfig(g.config, 'visibilityRule') ?? regraVazia('visibility'),
    })

  const salvarAba = () => {
    if (!grupoRascunho) return
    const nome = grupoRascunho.nome.trim()
    if (!nome) return toast.push({ tone: 'danger', message: 'Informe o nome do tópico.' })
    salvarGrupo.mutate(
      {
        id: grupoRascunho.id,
        // é o TÓPICO que diz a que entidade o campo pertence (fase 6)
        entity: grupoRascunho.entity,
        kind: 'topic',
        labels: { 'pt-BR': nome },
        config: {
          icon: grupoRascunho.iconImageId ? '' : grupoRascunho.icon,
          iconImageId: grupoRascunho.iconImageId,
          description: grupoRascunho.descricao.trim(),
          ...(grupoRascunho.instrucoes.trim() !== '' && grupoRascunho.instrucoes !== '<p></p>'
            ? { help: grupoRascunho.instrucoes }
            : {}),
          viewType: grupoRascunho.viewType,
          ...(grupoRascunho.entity === 'work_item' && grupoRascunho.workItemTypeIds.length > 0
            ? { workItemTypeIds: grupoRascunho.workItemTypeIds }
            : {}),
          ...(grupoRascunho.visibilityRule.criteria.length > 0
            ? { visibilityRule: grupoRascunho.visibilityRule }
            : {}),
        },
      } as never,
      {
        onSuccess: () => {
          setGrupoRascunho(null)
          toast.push({ tone: 'success', message: 'Tópico salvo.' })
        },
        onError: (e) => toast.push({ tone: 'danger', message: e instanceof Error ? e.message : 'Falha ao salvar o tópico.' }),
      },
    )
  }

  const removerAba = (g: CustomFieldGroup) =>
    confirm.ask('O tópico será apagado. Os campos dele voltam para a barra de propriedades.', () =>
      apagarGrupo.mutate(g.id),
    )

  // condições de UMA colocação (engrenagem no card do campo)
  const abrirConfigColocacao = (placement: CustomFieldPlacement, campo: CustomField) =>
    setRegrasColoc({
      id: placement.id,
      nome: rotuloDe(campo.labels, campo.key),
      visibilityRule: regraDoConfig(placement.config, 'visibilityRule') ?? regraVazia('visibility'),
      requiredRule: regraDoConfig(placement.config, 'requiredRule') ?? regraVazia('required'),
    })
  const salvarRegrasColoc = () => {
    if (!regrasColoc) return
    const config: Record<string, unknown> = {}
    if (regrasColoc.visibilityRule.criteria.length > 0) config.visibilityRule = regrasColoc.visibilityRule
    if (regrasColoc.requiredRule.criteria.length > 0) config.requiredRule = regrasColoc.requiredRule
    salvarConfigColocacao.mutate(
      { id: regrasColoc.id, config },
      {
        onSuccess: () => {
          setRegrasColoc(null)
          toast.push({ tone: 'success', message: 'Condições salvas.' })
        },
        onError: (e) => toast.push({ tone: 'danger', message: e instanceof Error ? e.message : 'Falha ao salvar.' }),
      },
    )
  }

  // barra de propriedades + uma aba por grupo, cada um como accordion
  /**
   * A "barra de propriedades" é do detalhe do Item de Trabalho — Empresa e
   * Pessoa não têm barra, os campos delas vivem nos tópicos do cadastro. Por
   * isso esse accordion só entra em `work_item` (fase 6).
   */
  /**
   * Prévia da máscara personalizada: as prontas já mostram o exemplo no próprio
   * rótulo da lista, então só o padrão livre precisa de um "fica assim".
   */
  /**
   * O endereço do botão pode citar campos entre chaves, então a validação usa
   * um valor de mentira nos marcadores — senão todo template pareceria quebrado
   * enquanto os campos estivessem vazios.
   */
  const urlDoBotaoOk =
    rascunho?.type === 'button'
      ? montarUrlDoBotao(
          rascunho.botaoUrl,
          Object.fromEntries(chavesDoTemplate(rascunho.botaoUrl).map((k) => [k, 'x'])),
        ) !== null
      : true

  const previaMascara =
    rascunho?.type === 'text' && rascunho.mascara === 'custom'
      ? (mascaraDoCampo({ mask: rascunho.mascara, maskPattern: rascunho.mascaraPadrao })?.exemplo ?? '')
      : ''

  /**
   * Tipos de item a que o tópico pertence. Sem nenhum marcado o tópico não
   * aparece em item algum — dizer "nenhum tipo" evita a confusão de um tópico
   * configurado que nunca se mostra.
   */
  const tiposDoGrupo = (config: Record<string, unknown> | undefined) => {
    const ids = config?.['workItemTypeIds']
    if (!Array.isArray(ids) || ids.length === 0) return 'Nenhum tipo'
    const nomes = (boot.data?.workItemTypes ?? [])
      .filter((tp) => ids.includes(tp.id))
      .map((tp) => t(tp.nameTranslationKey))
    if (nomes.length === 0) return 'Nenhum tipo'
    return nomes.length <= 2 ? nomes.join(' · ') : `${nomes.length} tipos`
  }

  const accordions = [
    ...(ENTITY === 'work_item' ? [{ id: 'prop', grupo: null as CustomFieldGroup | null }] : []),
    ...grupos.map((g) => ({ id: g.id, grupo: g as CustomFieldGroup | null })),
  ]

  return (
    /* com a lista aberta a área de trabalho é toda dela: sem construtor e sem
       painel lateral (pedido 2026-08-30). Os modais seguem montados, para que
       "Editar campo" da lista abra a mesma tela de sempre. */
    <div
      className={verLista ? 'ccf' : 'ccf ccf--cols'}
      style={verLista ? undefined : { gridTemplateColumns: `minmax(0, 1fr) 10px ${catLargura}px` }}
    >
      {verLista ? (
        <ListaDeCampos
          onFechar={() => setVerLista(false)}
          onEditarCampo={(campo) => {
            setEntidadeAtiva(campo.entity as Entidade)
            abrirEdicao(campo)
          }}
        />
      ) : (
        <>
      <div className="ccf__main">
      {/* ENTIDADE da tela (fase 6): tudo abaixo — tópicos, campos e catálogo —
          é da entidade escolhida aqui. */}
      <div className="ccf__abas-entidade" role="tablist" aria-label="Entidade dos campos">
        {ENTIDADES.map((op) => (
          <button
            key={op.value}
            type="button"
            role="tab"
            aria-selected={ENTITY === op.value}
            className={`ccf__aba-entidade${ENTITY === op.value ? ' ccf__aba-entidade--on' : ''}`}
            onClick={() => trocarEntidade(op.value)}
          >
            <i className={op.icon} aria-hidden />
            {op.label}
          </button>
        ))}
      </div>

      <div className="ccf__topo">
        <p className="ccf__intro">
          {ENTITY === 'work_item'
            ? 'Campos próprios para os Itens de Trabalho, por tipo. Arraste do catálogo (à direita) para um tópico ou para a barra de propriedades — o mesmo campo pode ficar em vários tópicos.'
            : `Campos próprios de ${ENTIDADE_LABEL[ENTITY]}. Cada tópico vira uma seção do cadastro; crie o campo pelo “+” do tópico ou arraste um do catálogo.`}
        </p>
        <div className="ccf__topo-acoes">
          <Button variant="secondary" icon="fas fa-folder-plus" onClick={novaAba}>
            Novo tópico
          </Button>
        </div>
      </div>

      {/* accordions: cada um é um tópico (o cabeçalho É o tópico); o primeiro é a
          barra de propriedades. Solte um campo do catálogo aqui para colocá-lo. */}
      <div className="ccf__accs">
        {accordions.map(({ id, grupo }) => {
          const aberto = !recolhidos.has(id)
          const grupoId = grupo ? grupo.id : null
          const itens = colocacoesDe(grupoId)
          const nome = grupo ? rotuloDe(grupo.labels, 'Tópico') : 'Barra de propriedades'
          const modo = grupo ? viewTypeDe(grupo.config) : null
          const icone = grupo ? iconeDoGrupo(grupo.config) : 'fas fa-sliders-h'
          const descricao = grupo ? descricaoDoGrupo(grupo.config) : ''
          return (
            <div
              key={id}
              className={`ccf__acc${sobre === id ? ' ccf__acc--sobre' : ''}`}
              onDragOver={(e) => {
                if (e.dataTransfer.types.includes(DRAG_MIME)) {
                  e.preventDefault()
                  e.dataTransfer.dropEffect = 'copy'
                  if (sobre !== id) setSobre(id)
                }
              }}
              onDragLeave={(e) => {
                if (!e.currentTarget.contains(e.relatedTarget as Node)) setSobre((s) => (s === id ? null : s))
              }}
              onDrop={soltarNoGrupo(grupoId)}
            >
              <div className="ccf__acc-head">
                <button
                  type="button"
                  className="ccf__acc-toggle"
                  aria-expanded={aberto}
                  onClick={() => alternar(id)}
                >
                  <i className={`fas fa-chevron-${aberto ? 'down' : 'right'} ccf__acc-chev`} aria-hidden />
                  <CatalogIcon icon={icone} fallback={grupo ? 'fas fa-folder' : 'fas fa-sliders-h'} size={13} />
                  <span className="ccf__acc-nome">{nome}</span>
                  {/* a que TIPOS de item o tópico pertence, antes do modo
                      (pedido 2026-08-31): é o que decide em quais itens ele
                      aparece, então vem primeiro */}
                  {grupo ? <span className="ccf__tipos-badge">{tiposDoGrupo(grupo.config)}</span> : null}
                  {modo ? <span className="ccf__modo">{MODO_ROTULO[modo]}</span> : null}
                  <span className="ccf__tabcount">{itens.length}</span>
                </button>
                {/* cria um campo NOVO já dentro deste tópico (ou da barra) —
                    sem passar pelo catálogo e arrastar (pedido 2026-08-30) */}
                <button
                  type="button"
                  className="ccf__acc-add"
                  title={grupo ? 'Adicionar campo neste tópico' : 'Adicionar campo na barra de propriedades'}
                  aria-label={grupo ? `Adicionar campo em ${nome}` : 'Adicionar campo na barra de propriedades'}
                  onClick={(e) => {
                    e.stopPropagation()
                    setRecolhidos((s) => {
                      const n = new Set(s)
                      n.delete(id)
                      return n
                    })
                    setChaveTocada(false)
                    setRascunho({ ...vazio(ENTITY), destinoGrupoId: grupoId })
                  }}
                >
                  <i className="fas fa-plus" aria-hidden />
                </button>
                {grupo ? (
                  <button
                    type="button"
                    className={`ccf__acc-cfg${regraDoConfig(grupo.config, 'visibilityRule') ? ' ccf__acc-cfg--on' : ''}`}
                    title="Configurar o tópico (nome, tipo de visualização, ícone, condições)"
                    aria-label="Configurar tópico"
                    onClick={() => editarAba(grupo)}
                  >
                    <i className="fas fa-cog" aria-hidden />
                  </button>
                ) : null}
              </div>
              {aberto ? (
                <div className="ccf__acc-body">
                  {descricao ? <p className="ccf__abahead-desc">{descricao}</p> : null}
                  {itens.length === 0 ? (
                    <p className="ccf__vazio">Use o “+” para criar um campo aqui, ou arraste um do catálogo.</p>
                  ) : (
                    <AbaLayoutEditor
                      itens={itens}
                      onSalvarLayout={(x) => salvarLayout.mutate(x)}
                      onEditar={abrirEdicao}
                      onRemover={(pid) => removerColocacao.mutate(pid)}
                      onConfig={abrirConfigColocacao}
                    />
                  )}
                  {grupo ? (
                    <div className="ccf__acc-foot">
                      <button type="button" className="ccf__link" onClick={() => editarAba(grupo)}>
                        <i className="fas fa-pen" aria-hidden /> Editar tópico
                      </button>
                      <button type="button" className="ccf__link ccf__link--perigo" onClick={() => removerAba(grupo)}>
                        <i className="fas fa-trash" aria-hidden /> Excluir tópico
                      </button>
                    </div>
                  ) : null}
                </div>
              ) : null}
            </div>
          )
        })}
      </div>
      </div>

      <div
        className="ccf__split"
        role="separator"
        aria-orientation="vertical"
        aria-label="Redimensionar o catálogo"
        title="Arraste para redimensionar · duplo clique alterna entre estreito e largo"
        tabIndex={0}
        onDoubleClick={alternarLarguraCatalogo}
        // teclado: setas ajustam a largura sem precisar do mouse
        onKeyDown={(e) => {
          if (e.key === 'ArrowLeft') setCatLargura((l) => limitaLargura(l + 40))
          if (e.key === 'ArrowRight') setCatLargura((l) => limitaLargura(l - 40))
        }}
        onPointerDown={(e) => {
          ;(e.currentTarget as HTMLElement).setPointerCapture(e.pointerId)
          arrasteDivisor.current = { x: e.clientX, larg: catLargura }
        }}
        onPointerMove={aoArrastarDivisor}
        onPointerUp={(e) => {
          ;(e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId)
          arrasteDivisor.current = null
        }}
      >
        <span className="ccf__split-grip" aria-hidden />
      </div>

      <CatalogoCampos
        onNovoCampo={(entity, folderId) => {
          setChaveTocada(false)
          setRascunho({ ...vazio(entity), catalogGroupId: folderId ?? null })
        }}
        // "Criar campo" do rodapé: abre a tela já no tipo escolhido
        onNovoCampoDoTipo={(tipo) => {
          setChaveTocada(false)
          setRascunho({ ...vazio('work_item'), type: tipo })
        }}
        onEditarCampo={abrirEdicao}
        onAbrirLista={() => setVerLista(true)}
      />
        </>
      )}

      {/* -------------------------------------------------- modal de campo */}
      {rascunho ? (
        <Modal
          open
          onClose={() => setRascunho(null)}
          title={
            rascunho.id
              ? 'Editar campo'
              : rascunho.destinoGrupoId !== undefined
                ? // criado a partir de um tópico/barra: diz onde vai entrar
                  `Novo campo em ${
                    rascunho.destinoGrupoId
                      ? rotuloDe(grupos.find((g) => g.id === rascunho.destinoGrupoId)?.labels ?? {}, 'Tópico')
                      : 'Barra de propriedades'
                  }`
                : `Novo campo · ${ENTIDADE_LABEL[rascunho.entity]}`
          }
          footer={
            <>
              {rascunho.id ? (
                <Button
                  variant="ghost"
                  icon="fas fa-trash"
                  onClick={() => {
                    const alvo = campoPorId.get(rascunho.id!)
                    if (alvo) {
                      setRascunho(null)
                      void removerCampo(alvo)
                    }
                  }}
                >
                  Excluir campo
                </Button>
              ) : null}
              <span style={{ flex: 1 }} />
              <Button variant="ghost" onClick={() => setRascunho(null)}>
                Cancelar
              </Button>
              <Button variant="primary" loading={salvarCampo.isPending} onClick={salvar}>
                Salvar
              </Button>
            </>
          }
        >
          <FieldBlock label="Rótulo (pt-BR)">
            {({ id }) => (
              <Input
                id={id}
                autoFocus
                value={rascunho.label}
                placeholder="Ex.: Código externo"
                onChange={(e) =>
                  setRascunho((r) =>
                    r ? { ...r, label: e.target.value, key: chaveTocada ? r.key : chaveDe(e.target.value) } : r,
                  )
                }
              />
            )}
          </FieldBlock>
          <FieldBlock label="Chave (usada nos filtros)">
            {({ id }) => (
              <Input
                id={id}
                value={rascunho.key}
                placeholder="codigo_externo"
                onChange={(e) => {
                  setChaveTocada(true)
                  setRascunho((r) => (r ? { ...r, key: e.target.value } : r))
                }}
              />
            )}
          </FieldBlock>
          {/* tipo do campo em CARDS (ícone + nome), em vez de um select seco —
              fica claro o que cada tipo é (pedido 2026-08-30) */}
          <FieldBlock label="Tipo do campo">
            {() => (
              <div className="ccf__tipos-grid">
                {TIPOS_CAMPO.map((tp) => (
                  <button
                    key={tp.value}
                    type="button"
                    className={`ccf__tipocard${rascunho.type === tp.value ? ' ccf__tipocard--on' : ''}`}
                    aria-pressed={rascunho.type === tp.value}
                    title={tp.hint}
                    onClick={() => setRascunho((r) => (r ? { ...r, type: tp.value } : r))}
                  >
                    <i className={tp.icon} aria-hidden />
                    <span className="ccf__tipocard-nome">{tp.label}</span>
                  </button>
                ))}
              </div>
            )}
          </FieldBlock>
          {/* lista x tag: só o layout muda, as opções e os valores são os mesmos */}
          {rascunho.type === 'select' || rascunho.type === 'multiselect' ? (
            <FieldBlock label="Formato do campo" hint="Muda só a aparência — as opções continuam as mesmas.">
              {() => (
                <div className="ccf__layout-esc" role="group">
                  {LAYOUTS_ESCOLHA.map((op) => (
                    <button
                      key={op.value}
                      type="button"
                      className={`ccf__layout-op${rascunho.layout === op.value ? ' ccf__layout-op--on' : ''}`}
                      aria-pressed={rascunho.layout === op.value}
                      onClick={() => setRascunho((r) => (r ? { ...r, layout: op.value } : r))}
                    >
                      <i className={op.icon} aria-hidden />
                      {op.label}
                    </button>
                  ))}
                </div>
              )}
            </FieldBlock>
          ) : null}
          {rascunho.type === 'button' ? (
            <>
              <FieldBlock label="Texto do botão">
                {({ id }) => (
                  <Input
                    id={id}
                    value={rascunho.botaoRotulo}
                    placeholder="Ex.: Consultar na Receita"
                    onChange={(e) => setRascunho((r) => (r ? { ...r, botaoRotulo: e.target.value } : r))}
                  />
                )}
              </FieldBlock>
              <FieldBlock
                label="Endereço que o botão abre"
                hint="Use {chave} para levar o valor de outro campo — ex.: https://consulta.gov/{cnpj_secundario}."
              >
                {({ id }) => (
                  <Input
                    id={id}
                    value={rascunho.botaoUrl}
                    placeholder="https://exemplo.com.br/{chave}"
                    inputMode="url"
                    invalid={rascunho.botaoUrl.trim() !== '' && !urlDoBotaoOk}
                    onChange={(e) => setRascunho((r) => (r ? { ...r, botaoUrl: e.target.value } : r))}
                  />
                )}
              </FieldBlock>
              {rascunho.botaoUrl.trim() !== '' && !urlDoBotaoOk ? (
                <p className="ccf__mascara-previa">Endereço inválido — o botão fica desligado assim.</p>
              ) : null}
            </>
          ) : null}
          {rascunho.type === 'select' || rascunho.type === 'multiselect' ? (
            <FieldBlock label="Opções (uma por linha)">
              {({ id }) => (
                <Textarea
                  id={id}
                  rows={3}
                  value={rascunho.opcoes}
                  placeholder={'Opção A\nOpção B'}
                  onChange={(e) => setRascunho((r) => (r ? { ...r, opcoes: e.target.value } : r))}
                />
              )}
            </FieldBlock>
          ) : null}
          {/* máscara: só o texto curto formata enquanto digita (pedido 2026-08-30) */}
          {rascunho.type === 'text' ? (
            <>
              <FieldBlock label="Máscara" hint="Formata o valor enquanto a pessoa digita.">
                {({ id }) => (
                  <Select
                    id={id}
                    value={rascunho.mascara}
                    options={[
                      { value: '', label: 'Sem máscara' },
                      ...MASCARAS_PRONTAS.map((m) => ({
                        value: m.key,
                        label: m.exemplo ? `${m.label} — ${m.exemplo}` : m.label,
                      })),
                    ]}
                    onChange={(val) => setRascunho((r) => (r ? { ...r, mascara: val } : r))}
                  />
                )}
              </FieldBlock>
              {rascunho.mascara === 'custom' ? (
                <FieldBlock
                  label="Padrão da máscara"
                  hint="0 = número · A = letra maiúscula · a = letra minúscula · S = letra ou número · X = letra ou número em maiúscula. Qualquer outro caractere é fixo; use \ antes para tratá-lo como texto."
                >
                  {({ id }) => (
                    <Input
                      id={id}
                      value={rascunho.mascaraPadrao}
                      placeholder="Ex.: AA-0000/00"
                      onChange={(e) => setRascunho((r) => (r ? { ...r, mascaraPadrao: e.target.value } : r))}
                    />
                  )}
                </FieldBlock>
              ) : null}
              {previaMascara ? <p className="ccf__mascara-previa">Fica assim: {previaMascara}</p> : null}
            </>
          ) : null}
          <label className="ccf__check">
            <input
              type="checkbox"
              checked={rascunho.required}
              onChange={(e) => setRascunho((r) => (r ? { ...r, required: e.target.checked } : r))}
            />
            Obrigatório para salvar
          </label>
          <label className="ccf__check">
            <input
              type="checkbox"
              checked={rascunho.defaultVisible}
              onChange={(e) => setRascunho((r) => (r ? { ...r, defaultVisible: e.target.checked } : r))}
            />
            Visível por padrão
          </label>
          {/* instrucoes de preenchimento: o PADRAO do "i" que aparece ao lado
              do campo nos itens; cada item pode personalizar por cima */}
          <FieldBlock
            label="Instruções de preenchimento"
            hint="Aparecem no “i” ao lado do campo nos Itens de Trabalho. Cada item pode personalizar a sua versão."
          >
            {() => (
              <RichTextEditor
                value={rascunho.instrucoes}
                placeholder="Explique o que vai neste campo e como preencher…"
                minHeight={120}
                onSave={(html) => setRascunho((r) => (r ? { ...r, instrucoes: html } : r))}
              />
            )}
          </FieldBlock>
        </Modal>
      ) : null}

      {/* --------------------------------------------------- modal de tópico */}
      {grupoRascunho ? (
        <Modal
          open
          onClose={() => setGrupoRascunho(null)}
          title={grupoRascunho.id ? 'Editar tópico' : 'Novo tópico'}
          footer={
            <>
              <Button variant="ghost" onClick={() => setGrupoRascunho(null)}>
                Cancelar
              </Button>
              <Button variant="primary" loading={salvarGrupo.isPending} onClick={salvarAba}>
                Salvar
              </Button>
            </>
          }
        >
          <FieldBlock label="Nome do tópico (pt-BR)">
            {({ id }) => (
              <Input
                id={id}
                autoFocus
                value={grupoRascunho.nome}
                placeholder="Ex.: Atendimento"
                onChange={(e) => setGrupoRascunho((g) => (g ? { ...g, nome: e.target.value } : g))}
              />
            )}
          </FieldBlock>
          {/* tipo de visualização: simples (tópico solto), aba (lado a lado) ou
              formulário (botão na barra de propriedades + modal) */}
          <FieldBlock label="Tipo de visualização">
            {({ id }) => (
              <Select
                id={id}
                value={grupoRascunho.viewType}
                onChange={(v) => setGrupoRascunho((g) => (g ? { ...g, viewType: (v as ViewType) ?? 'simple' } : g))}
                options={[
                  { value: 'simple', label: 'Simples — tópico recolhível, sozinho' },
                  { value: 'tab', label: 'Aba — lado a lado com os outros tópicos-aba' },
                  { value: 'form', label: 'Formulário — botão na barra de propriedades que abre um modal' },
                ]}
              />
            )}
          </FieldBlock>
          <IconField
            label="Ícone do tópico"
            value={grupoRascunho.icon}
            imageId={grupoRascunho.iconImageId}
            onChange={({ icon, iconImageId }) =>
              setGrupoRascunho((g) => (g ? { ...g, icon, iconImageId } : g))
            }
          />
          <FieldBlock label="Descrição (opcional)">
            {({ id }) => (
              <Textarea
                id={id}
                rows={2}
                value={grupoRascunho.descricao}
                placeholder="Explique o que este tópico agrupa."
                onChange={(e) => setGrupoRascunho((g) => (g ? { ...g, descricao: e.target.value } : g))}
              />
            )}
          </FieldBlock>
          {/* padrao do "i" do topico no item; personalizavel por item */}
          <FieldBlock label="Instruções de preenchimento" hint="Aparecem no “i” ao lado do tópico no item.">
            {() => (
              <RichTextEditor
                value={grupoRascunho.instrucoes}
                minHeight={100}
                placeholder="Explique como os campos deste tópico devem ser preenchidos…"
                onSave={(html) => setGrupoRascunho((g) => (g ? { ...g, instrucoes: html } : g))}
              />
            )}
          </FieldBlock>
          {/* A ENTIDADE do topico e o que liga o campo (fase 6): Item de
              Trabalho, Empresa ou Pessoa. Empresa e Pessoa nao tem "tipo". */}
          <FieldBlock label="Este tópico é de" hint="Define onde os campos dele aparecem.">
            {() => (
              <div className="ccf__entidades" role="radiogroup" aria-label="Entidade do tópico">
                {ENTIDADES.map((op) => (
                  <button
                    key={op.value}
                    type="button"
                    role="radio"
                    aria-checked={grupoRascunho.entity === op.value}
                    className={`ccf__entidade${grupoRascunho.entity === op.value ? ' ccf__entidade--on' : ''}`}
                    onClick={() => setGrupoRascunho((g) => (g ? { ...g, entity: op.value } : g))}
                  >
                    <i className={op.icon} aria-hidden />
                    <span>
                      <strong>{op.label}</strong>
                      <small>{op.hint}</small>
                    </span>
                  </button>
                ))}
              </div>
            )}
          </FieldBlock>

          {/* tipos de item so existem para Item de Trabalho */}
          {grupoRascunho.entity === 'work_item' ? (
          <FieldBlock label="Tipos de item a que o tópico pertence">
            {() => (
              <>
                <span className="ccf__regra-ajuda" style={{ display: 'block', marginBottom: 8 }}>
                  Sem nenhum tipo marcado, o tópico não aparece em item algum.
                </span>
                <div className="ccf__tipos">
                  {(boot.data?.workItemTypes ?? [])
                    .filter((tp) => tp.isActive)
                    .map((tp) => {
                      const on = grupoRascunho.workItemTypeIds.includes(tp.id)
                      const rot = t(tp.nameTranslationKey)
                      return (
                        <button
                          key={tp.id}
                          type="button"
                          className={`ccf__tipo${on ? ' ccf__tipo--on' : ''}`}
                          aria-pressed={on}
                          onClick={() =>
                            setGrupoRascunho((g) =>
                              g
                                ? {
                                    ...g,
                                    workItemTypeIds: on
                                      ? g.workItemTypeIds.filter((x) => x !== tp.id)
                                      : [...g.workItemTypeIds, tp.id],
                                  }
                                : g,
                            )
                          }
                        >
                          <CatalogIcon icon={tp.icon} fallback="fas fa-tasks" size={12} />
                          {rot === tp.nameTranslationKey ? tp.code : rot}
                          {on ? <i className="fas fa-check" aria-hidden style={{ fontSize: 10, marginLeft: 2 }} /> : null}
                        </button>
                      )
                    })}
                </div>
              </>
            )}
          </FieldBlock>
          ) : null}
          {/* condição de exibição do TÓPICO (ex.: aparecer só numa etapa/área) */}
          <div className="ccf__regra">
            <span className="ccf__regra-tit">Mostrar o tópico quando</span>
            <span className="ccf__regra-ajuda">Sem critérios, o tópico sempre aparece.</span>
            <FilterEditor
              fields={camposCriterio}
              value={grupoRascunho.visibilityRule}
              onChange={(f) => setGrupoRascunho((g) => (g ? { ...g, visibilityRule: f } : g))}
              loadValueOptions={carregarOpcoes}
              allowEmpty
            />
          </div>
        </Modal>
      ) : null}

      {/* ------------------------------------- modal de condições da colocação */}
      {regrasColoc ? (
        <Modal
          open
          onClose={() => setRegrasColoc(null)}
          title={`Condições · ${regrasColoc.nome}`}
          footer={
            <>
              <Button variant="ghost" onClick={() => setRegrasColoc(null)}>
                Cancelar
              </Button>
              <Button variant="primary" loading={salvarConfigColocacao.isPending} onClick={salvarRegrasColoc}>
                Salvar
              </Button>
            </>
          }
        >
          <div className="ccf__regra ccf__regra--top">
            <span className="ccf__regra-tit">Mostrar quando</span>
            <span className="ccf__regra-ajuda">Sem critérios, o campo sempre aparece aqui.</span>
            <FilterEditor
              fields={camposCriterio}
              value={regrasColoc.visibilityRule}
              onChange={(f) => setRegrasColoc((r) => (r ? { ...r, visibilityRule: f } : r))}
              loadValueOptions={carregarOpcoes}
              allowEmpty
            />
          </div>
          <div className="ccf__regra">
            <span className="ccf__regra-tit">Exigir quando</span>
            <span className="ccf__regra-ajuda">Torna o preenchimento obrigatório só quando os critérios batem.</span>
            <FilterEditor
              fields={camposCriterio}
              value={regrasColoc.requiredRule}
              onChange={(f) => setRegrasColoc((r) => (r ? { ...r, requiredRule: f } : r))}
              loadValueOptions={carregarOpcoes}
              allowEmpty
            />
          </div>
        </Modal>
      ) : null}

      {confirm.element}
    </div>
  )
}
